import {
  type APIComponentAttributes,
  type APIReferenceAttributes,
  type APIReferenceType,
  type APIConstraintEnforcementMode,
  ArdoqId,
} from '@ardoq/api-types';
import { type PermissionContext } from '@ardoq/access-control';
import { spreadMap } from '@ardoq/rxbeach';
import { intendedRefTypes$ } from 'appModelStateEdit/propertiesEditor/intendedRefTypes$';
import { combineLatest } from 'rxjs';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { constraintsEnforcementMode$ } from 'streams/constraintsEnforcementMode$';
import { context$ } from 'streams/context/context$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { references$ } from 'streams/references/references$';
import { components$ } from 'streams/components/components$';
import {
  type ComponentTypesByWorkspaceId,
  componentTypesByWorkspaceId$,
} from 'streams/models/componentTypesByWorkspaceId$';
import {
  type ReferenceTypesByWorkspaceId,
  referenceTypesByWorkspaceId$,
} from 'streams/models/referenceTypesByWorkspaceId$';
import { type StreamCollection } from 'streams/utils/streamUtils';
import { ReverseConstraintViolation } from './referenceOptionsConstraints';
import { mapContextToReferenceOptions } from './referenceOptionsHelpers';
import { type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { type ContextShape, type ScenarioModeState } from '@ardoq/data-model';
import { type IntendedRefTypes } from 'utils/metamodelConstraints';

export interface ReferenceModel {
  _id: ArdoqId;
  leftName: string;
  rightName: string;
  arrowDirection: string;
}

export type ToggleLockCapability =
  | { canToggleLock: false }
  | { canToggleLock: true; currentUserId: ArdoqId };

export interface ReferenceOptionsState {
  references: ReferenceModel[];
  hasWorkspaceWriteAccess: boolean;
  canEditReference: boolean;
  canEditReferencesTargetComponent: boolean;
  isViewVersionHistoryDisabled: boolean;
  contextReferenceId: ArdoqId | undefined;
  locked: boolean;
  toggleLock: ToggleLockCapability;
  reverseConstraintViolation: ReverseConstraintViolation;
  i18nLocale: I18nLocale;
}

const getComponentTypeName = (
  component: APIComponentAttributes | undefined,
  componentTypesByWorkspaceId: ComponentTypesByWorkspaceId
): string | null => {
  if (!component) {
    return null;
  }
  return (
    componentTypesByWorkspaceId[component.rootWorkspace]?.find(
      componentType => componentType.id === component.typeId
    )?.name ?? null
  );
};

const toReferenceOptions = (
  contextState: ContextShape,
  permissionContext: PermissionContext,
  activeScenarioState: ScenarioModeState,
  intendedRefTypes: IntendedRefTypes | null,
  constraintEnforcementMode: APIConstraintEnforcementMode,
  i18nLocale: I18nLocale,
  references: StreamCollection<APIReferenceAttributes>,
  components: StreamCollection<APIComponentAttributes>,
  componentTypesByWorkspaceId: ComponentTypesByWorkspaceId,
  referenceTypesByWorkspaceId: ReferenceTypesByWorkspaceId
): ReferenceOptionsState => {
  const allRefs = references.models;
  const uniqueComponentIds = [
    ...new Set(allRefs.flatMap(ref => [ref.source, ref.target])),
  ];
  const displayNameById = new Map<ArdoqId, string | null>(
    uniqueComponentIds.map(id => [id, components.byId[id]?.name ?? null])
  );
  const reference = contextState.referenceId
    ? (references.byId[contextState.referenceId] ?? undefined)
    : undefined;
  const referenceTypes: readonly APIReferenceType[] = reference
    ? (referenceTypesByWorkspaceId[reference.rootWorkspace] ?? [])
    : [];
  return mapContextToReferenceOptions({
    contextState,
    permissionContext,
    activeScenarioState,
    intendedRefTypes,
    constraintEnforcementMode,
    i18nLocale,
    hasWorkspaceWriteAccess: workspaceAccessControlInterface.canEditWorkspace(
      permissionContext,
      contextState.workspaceId,
      activeScenarioState
    ),
    allRefs,
    reference,
    referenceTypes,
    sourceTypeName: reference
      ? getComponentTypeName(
          components.byId[reference.source],
          componentTypesByWorkspaceId
        )
      : null,
    targetTypeName: reference
      ? getComponentTypeName(
          components.byId[reference.target],
          componentTypesByWorkspaceId
        )
      : null,
    displayNameById,
    componentsById: components.byId,
  });
};

export const referenceOptions$ = combineLatest([
  context$,
  currentUserPermissionContext$,
  activeScenario$,
  intendedRefTypes$,
  constraintsEnforcementMode$,
  i18nLocale$,
  references$,
  components$,
  componentTypesByWorkspaceId$,
  referenceTypesByWorkspaceId$,
]).pipe(spreadMap(toReferenceOptions));
